import { isAllowedRelayOrigin, type OperatorConfig } from './config';

/**
 * 프리뷰 로그인 중계.
 * Auth 서버에는 고정 callback 하나만 등록한다. 프리뷰(origin이 매번 바뀜)는 state에 자기 origin을 실어 보내고,
 * 고정 callback이 code/state를 그 origin으로 되돌려 보낸다. code는 PKCE verifier를 가진 프리뷰만 교환할 수 있고,
 * 되돌릴 대상은 빌드 시 정한 허용 패턴으로 제한하므로 중계가 열린 리다이렉트가 되지 않는다.
 */
const NONCE = /^[A-Za-z0-9_-]{43}$/;
const ORIGIN_PART = /^[A-Za-z0-9_-]{1,200}$/;
const CALLBACK_PARAMS = ['code', 'state', 'error', 'error_description'] as const;
const MAX_PARAM_LENGTH = 2048;

const toBase64Url = (value: string) =>
  btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const fromBase64Url = (value: string) => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  return atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
};

/** `<nonce>.<base64url(origin)>` — Auth 서버는 state를 그대로 돌려주는 불투명 문자열로 다룬다. */
export function encodeRelayState(nonce: string, origin: string): string {
  return `${nonce}.${toBase64Url(origin)}`;
}

/** state에 실린 복귀 origin을 꺼낸다. 형식이 맞지 않으면 null(중계 없이 일반 callback으로 처리). */
export function decodeRelayOrigin(state: string): string | null {
  const [nonce, encoded, ...rest] = state.split('.');
  if (rest.length || !nonce || !encoded || !NONCE.test(nonce) || !ORIGIN_PART.test(encoded))
    return null;
  try {
    const origin = fromBase64Url(encoded);
    return new URL(origin).origin === origin ? origin : null;
  } catch {
    return null;
  }
}

/**
 * 등록 callback에 도착한 요청을 프리뷰 origin으로 되돌릴 주소를 계산한다. 중계 대상이 아니면 null.
 * 등록 origin·경로에서만 동작하므로 프리뷰 자신은 중계하지 않는다(루프 없음).
 */
export function resolveRelayTarget(
  href: string,
  config: Pick<OperatorConfig, 'redirectUri' | 'relayOrigins'>,
): string | null {
  try {
    const url = new URL(href);
    const registered = new URL(config.redirectUri);
    if (
      !config.relayOrigins.length ||
      url.origin !== registered.origin ||
      url.pathname !== registered.pathname ||
      url.hash
    )
      return null;
    const values = new Map<string, string>();
    for (const key of CALLBACK_PARAMS) {
      const all = url.searchParams.getAll(key);
      if (all.length > 1) return null;
      const value = all[0];
      if (value === undefined) continue;
      if (value.length > MAX_PARAM_LENGTH) return null;
      values.set(key, value);
    }
    const state = values.get('state');
    if (!state || !(values.has('code') || values.has('error'))) return null;
    const origin = decodeRelayOrigin(state);
    if (
      !origin ||
      origin === registered.origin ||
      !isAllowedRelayOrigin(origin, config.relayOrigins)
    )
      return null;
    const target = new URL(registered.pathname, origin);
    values.forEach((value, key) => {
      target.searchParams.set(key, value);
    });
    return target.href;
  } catch {
    return null;
  }
}
